import { useState, useEffect, useCallback, Fragment } from "react";
import {
  BarChart3,
  FileDown,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";

const fmtHours = (h) => `${Number(h || 0).toFixed(2).replace(/\.?0+$/, "")} h`;

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [branchId, setBranchId] = useState("all");
  const [companyId, setCompanyId] = useState("all");
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/companies").catch(() => ({ data: [] })),
    ])
      .then(([br, co]) => {
        setBranches(br.data || []);
        setCompanies(co.data || []);
      })
      .catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = { date_from: dateFrom, date_to: dateTo };
    if (branchId !== "all") params.branch_id = branchId;
    if (companyId !== "all") params.company_id = companyId;
    return params;
  }, [dateFrom, dateTo, branchId, companyId]);

  const generate = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Seleccione el rango de fechas");
      return;
    }
    setLoading(true);
    setExpanded({});
    try {
      const { data: res } = await api.get("/reports/hours-by-worker", {
        params: buildParams(),
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  }, [buildParams, dateFrom, dateTo]);

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportPdf = () => {
    const p = buildParams();
    const qs = new URLSearchParams(p).toString();
    window.open(`${apiBase}/api/reports/hours-by-worker/pdf?${qs}`, "_blank");
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const rows = data?.rows || [];
  const totals = data?.totals;

  return (
    <div data-testid="reports-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-zinc-900" strokeWidth={1.5} />
            <h1 className="text-2xl font-bold text-zinc-950">Reportes</h1>
          </div>
          <p className="text-sm text-zinc-500">
            Horas por trabajador y tiempos de comida en un rango de fechas.
          </p>
        </div>
        <Button
          onClick={exportPdf}
          disabled={!rows.length}
          className="gap-2"
          data-testid="reports-export-pdf"
        >
          <FileDown className="w-4 h-4" /> Exportar PDF
        </Button>
      </div>

      {/* Filtros */}
      <Card className="mb-5">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold text-zinc-700 mb-1.5 block">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="reports-date-from"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700 mb-1.5 block">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="reports-date-to"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700 mb-1.5 block">Sucursal</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger data-testid="reports-branch-select">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700 mb-1.5 block">Empresa</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="reports-company-select">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.commercial_name || c.legal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={loading} data-testid="reports-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <SummaryCard label="Trabajadores" value={totals.workers} testid="reports-total-workers" />
          <SummaryCard label="Horas totales" value={fmtHours(totals.total_hours)} testid="reports-total-hours" />
          <SummaryCard label="Horas extra" value={fmtHours(totals.overtime_hours)} testid="reports-total-ot" />
          <SummaryCard label="Días con exceso comida" value={totals.days_meal_exceeded} testid="reports-total-excess" />
        </div>
      )}

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Trabajador</TableHead>
                <TableHead className="text-center">Días lab.</TableHead>
                <TableHead className="text-center">H. Ordinarias</TableHead>
                <TableHead className="text-center">H. Extra</TableHead>
                <TableHead className="text-center">H. Totales</TableHead>
                <TableHead className="text-center">Tardanza (min)</TableHead>
                <TableHead className="text-center">Min. comida</TableHead>
                <TableHead className="text-center">Días exceso</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-zinc-400">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-zinc-400 text-sm">
                    No hay registros de asistencia en el rango seleccionado.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <Fragment key={r.employee_id}>
                    <TableRow
                      className="cursor-pointer hover:bg-zinc-50"
                      onClick={() => toggle(r.employee_id)}
                      data-testid={`reports-row-${r.employee_id}`}
                    >
                      <TableCell>
                        {expanded[r.employee_id] ? (
                          <ChevronDown className="w-4 h-4 text-zinc-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-zinc-400" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-zinc-900">{r.full_name}</div>
                        <div className="text-xs text-zinc-400 font-mono">{r.employee_code}</div>
                      </TableCell>
                      <TableCell className="text-center">{r.days_worked}</TableCell>
                      <TableCell className="text-center font-mono">{fmtHours(r.ordinary_hours)}</TableCell>
                      <TableCell className="text-center font-mono">{fmtHours(r.overtime_hours)}</TableCell>
                      <TableCell className="text-center font-mono font-semibold">{fmtHours(r.total_hours)}</TableCell>
                      <TableCell className="text-center font-mono">
                        {r.lateness_minutes_total > 0 ? (
                          <span className="text-red-600 font-semibold">{Math.round(r.lateness_minutes_total)}</span>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-mono">{Math.round(r.meal_minutes_total)}</TableCell>
                      <TableCell className="text-center">
                        {r.days_meal_exceeded > 0 ? (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {r.days_meal_exceeded}
                          </Badge>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded[r.employee_id] && (
                      <TableRow key={`${r.employee_id}-detail`} className="bg-zinc-50/60">
                        <TableCell colSpan={9} className="p-0">
                          <DailyDetail days={r.days} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, testid }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-zinc-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-zinc-950" data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}

function DailyDetail({ days }) {
  if (!days?.length) {
    return <div className="px-6 py-4 text-xs text-zinc-400">Sin días registrados.</div>;
  }
  return (
    <div className="px-6 py-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-zinc-500 border-b border-zinc-200">
            <th className="text-left py-1.5 font-medium">Fecha</th>
            <th className="text-center font-medium">Trabajadas</th>
            <th className="text-center font-medium">Ordinarias</th>
            <th className="text-center font-medium">Extra</th>
            <th className="text-center font-medium">Tardanza</th>
            <th className="text-left font-medium pl-4">Comidas</th>
            <th className="text-center font-medium">Total comida</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date} className="border-b border-zinc-100 last:border-0">
              <td className="py-1.5 font-mono text-zinc-700">{formatDate(d.date)}</td>
              <td className="text-center font-mono">{fmtHours(d.worked_hours)}</td>
              <td className="text-center font-mono">{fmtHours(d.ordinary_hours)}</td>
              <td className="text-center font-mono">{fmtHours(d.overtime_hours)}</td>
              <td className="text-center font-mono">
                {d.lateness_minutes > 0 ? (
                  <span className="text-red-600 font-semibold">{Math.round(d.lateness_minutes)}m</span>
                ) : (
                  <span className="text-zinc-300">—</span>
                )}
              </td>
              <td className="pl-4 text-zinc-600">
                {d.meals?.length
                  ? d.meals.map((m, i) => (
                      <span key={i} className="inline-block mr-2">
                        {m.label} {Math.round(m.minutes)}m
                      </span>
                    ))
                  : "—"}
              </td>
              <td className="text-center font-mono">
                <span className={d.meal_excess_minutes > 0 ? "text-amber-700 font-semibold" : ""}>
                  {Math.round(d.meal_total_minutes)} min
                </span>
                {d.meal_excess_minutes > 0 && (
                  <span className="text-amber-600"> (+{Math.round(d.meal_excess_minutes)})</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
